import { useEffect, useState, useCallback } from "react";
import { api, API, getToken } from "@/lib/api";
import { usePatient } from "@/context/PatientContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { IdentificationCard, Plus, PencilSimple, Trash, UploadSimple } from "@phosphor-icons/react";
import { toast } from "sonner";
import AuthImage from "@/components/AuthImage";

const empty = {
  provider: "", plan_name: "", policy_number: "", group_number: "", member_id: "",
  phone: "", start_date: "", end_date: "", notes: "",
  front_image_id: "", back_image_id: "",
};

export default function Insurance() {
  const { activePatient } = usePatient();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(empty);

  const load = useCallback(async () => {
    if (!activePatient) return;
    setLoading(true);
    try {
      const { data } = await api.get("/insurance_cards", { params: { patient_id: activePatient.id } });
      setItems(data);
    } finally { setLoading(false); }
  }, [activePatient]);
  useEffect(() => { load(); }, [load]);

  const startNew = () => { setForm(empty); setEditingId(null); setOpen(true); };
  const startEdit = (c) => { setForm({ ...empty, ...c }); setEditingId(c.id); setOpen(true); };
  const save = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, patient_id: activePatient.id };
      if (editingId) {
        await api.patch(`/insurance_cards/${editingId}`, payload);
        toast.success("Updated");
      } else {
        await api.post("/insurance_cards", payload);
        toast.success("Added");
      }
      setOpen(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    }
  };
  const remove = async (c) => {
    if (!window.confirm("Delete this insurance card?")) return;
    await api.delete(`/insurance_cards/${c.id}`);
    setItems((xs) => xs.filter((x) => x.id !== c.id));
    toast.success("Deleted");
  };
  const upload = async (file, which) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    const { data } = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
    setForm((f) => ({ ...f, [which]: data.id }));
    toast.success("Uploaded");
  };

  if (!activePatient) return <div className="text-[#576A62]">Add a patient first.</div>;

  return (
    <div className="space-y-8">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-[#576A62] font-semibold">Coverage</div>
          <h1 className="mt-2 font-heading text-3xl sm:text-4xl font-bold">Insurance cards</h1>
        </div>
        <button
          data-testid="insurance-add-btn"
          onClick={startNew}
          className="inline-flex items-center gap-2 rounded-full bg-[#2C5545] hover:bg-[#224235] text-white px-5 py-3 font-semibold"
        >
          <Plus weight="bold" size={16}/> Add card
        </button>
      </div>

      {loading ? (
        <div className="grid md:grid-cols-2 gap-4">{[0,1].map((i)=><div key={i} className="h-48 rounded-2xl bg-[#EBE8E0]/50 animate-pulse"/>)}</div>
      ) : items.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-[#EBE8E0] p-12 text-center bg-white">
          <IdentificationCard size={56} weight="duotone" className="text-[#2C5545] mx-auto" />
          <div className="mt-4 font-heading text-2xl font-bold">No insurance cards saved</div>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {items.map((c) => (
            <div key={c.id} data-testid={`insurance-card-${c.id}`} className="bg-white border border-[#EBE8E0] rounded-2xl p-5">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="font-heading text-lg font-bold truncate">{c.provider}</div>
                  <div className="text-sm text-[#576A62]">{c.plan_name || "—"}</div>
                </div>
                <IdentificationCard size={28} weight="duotone" className="text-[#2C5545]" />
              </div>
              <div className="mt-3 grid grid-cols-2 gap-3 text-sm">
                {c.policy_number && <Row k="Policy" v={c.policy_number} />}
                {c.group_number && <Row k="Group" v={c.group_number} />}
                {c.member_id && <Row k="Member ID" v={c.member_id} />}
                {c.phone && <Row k="Support" v={c.phone} />}
              </div>
              <div className="mt-4 grid grid-cols-2 gap-3">
                <CardImage id={c.front_image_id} label="Front" testid={`insurance-front-${c.id}`} />
                <CardImage id={c.back_image_id} label="Back" testid={`insurance-back-${c.id}`} />
              </div>
              <div className="mt-4 flex gap-2">
                <button onClick={() => startEdit(c)} data-testid={`insurance-edit-${c.id}`} className="flex-1 rounded-full border border-[#EBE8E0] hover:bg-[#EBE8E0]/60 px-3 py-1.5 text-sm inline-flex items-center justify-center gap-1"><PencilSimple size={14}/> Edit</button>
                <button onClick={() => remove(c)} data-testid={`insurance-delete-${c.id}`} className="rounded-full border border-[#EBE8E0] text-[#C86A51] hover:bg-[#C86A51]/10 px-3 py-1.5 text-sm"><Trash size={14}/></button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg rounded-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading">{editingId ? "Edit insurance card" : "Add insurance card"}</DialogTitle></DialogHeader>
          <form onSubmit={save} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <Field label="Provider" required value={form.provider} onChange={(v) => setForm({ ...form, provider: v })} testid="insurance-form-provider" placeholder="Blue Cross" />
              <Field label="Plan name" value={form.plan_name} onChange={(v) => setForm({ ...form, plan_name: v })} testid="insurance-form-plan" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Policy #" value={form.policy_number} onChange={(v) => setForm({ ...form, policy_number: v })} testid="insurance-form-policy" />
              <Field label="Group #" value={form.group_number} onChange={(v) => setForm({ ...form, group_number: v })} testid="insurance-form-group" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Member ID" value={form.member_id} onChange={(v) => setForm({ ...form, member_id: v })} testid="insurance-form-member" />
              <Field label="Support phone" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} testid="insurance-form-phone" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Start date" type="date" value={form.start_date} onChange={(v) => setForm({ ...form, start_date: v })} testid="insurance-form-start" />
              <Field label="End date" type="date" value={form.end_date} onChange={(v) => setForm({ ...form, end_date: v })} testid="insurance-form-end" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <ImageUploader label="Front of card" imageId={form.front_image_id} onFile={(f) => upload(f, "front_image_id")} testid="insurance-form-front" />
              <ImageUploader label="Back of card" imageId={form.back_image_id} onFile={(f) => upload(f, "back_image_id")} testid="insurance-form-back" />
            </div>
            <Field label="Notes" textarea value={form.notes} onChange={(v) => setForm({ ...form, notes: v })} testid="insurance-form-notes" />
            <DialogFooter>
              <button type="button" onClick={() => setOpen(false)} className="rounded-full px-5 py-2.5 border border-[#EBE8E0] text-[#576A62] hover:bg-[#EBE8E0]/60">Cancel</button>
              <button data-testid="insurance-form-save" type="submit" className="rounded-full px-5 py-2.5 bg-[#2C5545] hover:bg-[#224235] text-white font-semibold">Save</button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Row({ k, v }) {
  return <div><div className="text-xs uppercase tracking-widest text-[#576A62]">{k}</div><div className="font-medium truncate">{v}</div></div>;
}

function CardImage({ id, label, testid }) {
  if (!id) return <div className="rounded-xl border border-dashed border-[#EBE8E0] h-28 flex items-center justify-center text-sm text-[#576A62]">{label} not uploaded</div>;
  return (
    <div>
      <div className="text-xs uppercase tracking-widest text-[#576A62] mb-1">{label}</div>
      <AuthImage fileId={id} alt={label} className="rounded-xl w-full h-28 object-cover" data-testid={testid} />
    </div>
  );
}

function ImageUploader({ label, imageId, onFile, testid }) {
  return (
    <label className="block">
      <div className="text-sm font-medium mb-1.5">{label}</div>
      <div className="rounded-xl border border-[#EBE8E0] p-2 flex items-center gap-3 bg-white">
        {imageId ? (
          <AuthImage fileId={imageId} alt="" className="w-16 h-12 rounded object-cover" />
        ) : (
          <div className="w-16 h-12 rounded bg-[#EBE8E0]/40 flex items-center justify-center text-[#576A62]"><IdentificationCard size={20} /></div>
        )}
        <label className="flex-1 rounded-full bg-[#EBE8E0]/50 hover:bg-[#EBE8E0] text-[#1A2520] py-2 text-sm text-center cursor-pointer inline-flex items-center justify-center gap-1">
          <UploadSimple size={14} /> {imageId ? "Replace" : "Upload"}
          <input type="file" accept="image/*" className="hidden" onChange={(e) => onFile(e.target.files?.[0])} data-testid={testid} />
        </label>
      </div>
    </label>
  );
}

function Field({ label, value, onChange, type = "text", placeholder, required, testid, textarea }) {
  return (
    <label className="block">
      <div className="text-sm font-medium mb-1.5">{label}{required && <span className="text-[#C86A51]"> *</span>}</div>
      {textarea ? (
        <textarea data-testid={testid} value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
          rows={2} className="w-full rounded-xl border border-[#EBE8E0] bg-white px-4 py-3 focus:ring-2 focus:ring-[#2C5545] outline-none" />
      ) : (
        <input data-testid={testid} type={type} required={required} value={value ?? ""} onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder} className="w-full h-11 rounded-xl border border-[#EBE8E0] bg-white px-4 focus:ring-2 focus:ring-[#2C5545] outline-none" />
      )}
    </label>
  );
}
